import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ProductSearchOverlay from "./ProductSearchOverlay";
import { t } from "../../i18n";

const ADMIN_AUTH_UPDATED_EVENT = "ft-admin-auth-updated";
const USER_AUTH_UPDATED_EVENT = "ft-user-auth-updated";
const PRIVACY_MODE_STORAGE_KEY = "ft_privacy_mode";
const SEARCH_HISTORY_KEY = "ft_search_history";
const PRIVACY_MODE_ENHANCED = "enhanced";
const PRIVACY_MODE_BASIC = "basic";
const PRIVACY_SETTINGS_OPEN_EVENT = "ft-open-privacy-settings";

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Espanol" },
  { value: "fr", label: "Francais" },
  { value: "de", label: "Deutsch" }
];

const CURRENCY_OPTIONS = ["USD", "EUR", "GBP", "JPY"];

function getDefaultLayoutNavItems() {
  return [
    { key: "home", label: t("navHome"), path: "/" },
    { key: "products", label: t("navProducts"), path: "/products" },
    { key: "customization", label: t("navOemPrivateLabel"), path: "/custom-service" },
    { key: "solutions", label: t("navSolutions"), path: "/solutions" },
    { key: "about", label: t("navAbout"), path: "/about" },
    { key: "contact", label: t("navContactUs"), path: "/contact" }
  ];
}

function resolveLocalizedTopNavLabel(key, path, fallbackLabel) {
  const normalizedKey = String(key || "").trim().toLowerCase();
  const normalizedPath = String(path || "").trim().toLowerCase();

  if (normalizedKey === "home" || normalizedPath === "/") {
    return t("navHome");
  }

  if (normalizedKey === "products" || normalizedPath === "/products") {
    return t("navProducts");
  }

  if (
    normalizedKey === "customization" ||
    normalizedPath === "/custom-service" ||
    normalizedPath === "/oem-private-label" ||
    normalizedPath === "/#customization"
  ) {
    return t("navOemPrivateLabel");
  }

  if (normalizedKey === "solutions" || normalizedKey === "quality" || normalizedPath === "/solutions" || normalizedPath === "/#quality") {
    return t("navSolutions");
  }

  if (normalizedKey === "blog" || normalizedPath === "/blog" || normalizedPath === "/news") {
    return t("navBlog");
  }

  if (normalizedKey === "about" || normalizedPath === "/about") {
    return t("navCompanyProfile");
  }

  if (normalizedKey === "contact" || normalizedPath === "/contact") {
    return t("navContactUs");
  }

  return fallbackLabel;
}

const HEADER_POINTS = ["Verified Supply", "Private Label", "Global Shipping"];

function readPrivacyMode() {
  if (typeof window === "undefined") {
    return "";
  }

  const value = String(window.localStorage.getItem(PRIVACY_MODE_STORAGE_KEY) || "").trim().toLowerCase();
  if (value === PRIVACY_MODE_ENHANCED || value === PRIVACY_MODE_BASIC) {
    return value;
  }

  return "";
}

function writePrivacyMode(value) {
  if (typeof window === "undefined") {
    return;
  }

  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    window.localStorage.removeItem(PRIVACY_MODE_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(PRIVACY_MODE_STORAGE_KEY, normalized);
}

function formatBreadcrumbLabel(segment, options = {}) {
  if (!segment) {
    return "";
  }

  if (/^\d+$/.test(segment)) {
    const productTitle = String(options.productTitle || "").trim();
    return productTitle || t("breadcrumbProductDetail");
  }

  return segment
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getBreadcrumbItems(pathname, search, navItems, locationState, homeLabel = t("breadcrumbHomeSeries")) {
  const items = [{ label: t("breadcrumbHome"), path: "/" }];
  const segments = pathname.split("/").filter(Boolean);
  const flatNavItems = flattenNavItems(navItems);

  if (!segments.length) {
    items.push({ label: homeLabel });
    return items;
  }

  const matchedNavItem = flatNavItems.find((item) => {
    if (item.path === "/") {
      return pathname === "/";
    }

    return pathname === item.path || pathname.startsWith(`${item.path}/`);
  });

  if (matchedNavItem && matchedNavItem.path !== "/") {
    const normalizedLabel = matchedNavItem.path === "/products" ? "All Products" : matchedNavItem.label;
    items.push({ label: normalizedLabel, path: matchedNavItem.path });
  }

  if (pathname === "/products") {
    const params = new URLSearchParams(String(search || ""));
    const group = String(params.get("group") || "").trim();
    if (group) {
      const alreadyAdded = items.some((item) => String(item.label || "").trim().toLowerCase() === group.toLowerCase());
      if (!alreadyAdded) {
        items.push({ label: group });
      }
    }
  }

  if (pathname.startsWith("/products/") && segments.length > 1) {
    const groupFromState = String(locationState?.breadcrumbGroup || "").trim();
    if (groupFromState && !/^all\s*products$/i.test(groupFromState)) {
      const alreadyAdded = items.some((item) => String(item.label || "").trim().toLowerCase() === groupFromState.toLowerCase());
      if (!alreadyAdded) {
        items.push({
          label: groupFromState,
          path: `/products?group=${encodeURIComponent(groupFromState)}`
        });
      }
    }
  }

  if (segments.length > 1) {
    const detailLabel = formatBreadcrumbLabel(segments[segments.length - 1], {
      productTitle: locationState?.productTitle || locationState?.productName
    });
    if (!items.some((item) => item.label === detailLabel)) {
      items.push({ label: detailLabel, path: pathname });
    }
  }

  return items;
}

function buildGroupedNavItems(items) {
  if (!Array.isArray(items) || !items.length) {
    return [];
  }

  return items;
}

function normalizeNavTree(items, depth = 1, parentKey = "menu") {
  if (!Array.isArray(items) || depth > 3) {
    return [];
  }

  return items
    .map((item, index) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const key = String(item.key || `${parentKey}-${index + 1}`).trim();
      const label = String(item.label || "").trim();
      const path = String(item.path || "").trim();
      if (!key || !label || !path) {
        return null;
      }

      const normalizedKey = key.toLowerCase();
      const normalizedPath = path.toLowerCase();
      if (normalizedKey === "blog" || normalizedPath === "/blog" || normalizedPath === "/news") {
        return null;
      }

      const normalized = {
        key,
        label: resolveLocalizedTopNavLabel(key, path, label),
        path
      };
      normalized.visible = item.visible !== false;
      if (depth < 3) {
        const children = normalizeNavTree(item.children, depth + 1, `${key}-child`);
        if (children.length) {
          normalized.children = children;
        }
      }

      return normalized;
    })
    .filter(Boolean);
}

function flattenNavItems(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .filter((item) => item.visible !== false)
    .flatMap((item) => [item, ...flattenNavItems(item.children)]);
}

function collectNavNodes(items, parentIndex = "") {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .filter((item) => item.visible !== false)
    .flatMap((item, index) => {
    const indexPath = parentIndex ? `${parentIndex}-${index}` : String(index);
    return [{ item, index: indexPath }, ...collectNavNodes(item.children, indexPath)];
  });
}

function matchesNavPath(currentLocation, path) {
  if (!path) {
    return false;
  }

  const rawPath = String(path).trim();
  const [pathAndQuery, hashSegment = ""] = rawPath.split("#");
  const cleanPath = pathAndQuery || "/";
  const targetHash = hashSegment ? `#${hashSegment}` : "";
  const currentPath = `${currentLocation.pathname}${currentLocation.search || ""}`;
  const currentPathname = currentLocation.pathname;
  const currentHash = currentLocation.hash || "";

  if (cleanPath.includes("?")) {
    return currentPath === cleanPath && (!targetHash || currentHash === targetHash);
  }

  if (cleanPath === "/") {
    if (targetHash) {
      return currentPathname === "/" && currentHash === targetHash;
    }

    return currentPathname === "/" && !currentHash && !currentLocation.search;
  }

  const pathMatched = currentPathname === cleanPath || currentPathname.startsWith(`${cleanPath}/`);
  if (!pathMatched) {
    return false;
  }

  if (targetHash) {
    return currentHash === targetHash;
  }

  return true;
}

function normalizeNavigationTarget(to) {
  if (!to) {
    return "";
  }

  if (typeof to === "string") {
    return to;
  }

  const pathname = String(to.pathname || "").trim() || "/";
  const search = String(to.search || "").trim();
  const hash = String(to.hash || "").trim();
  return `${pathname}${search}${hash}`;
}

function parseNavigationTarget(target) {
  const normalized = String(target || "").trim();
  if (!normalized) {
    return { pathname: "", search: "", hash: "" };
  }

  const [pathAndSearch, hashPart = ""] = normalized.split("#");
  const [pathnamePart, searchPart = ""] = String(pathAndSearch || "").split("?");

  return {
    pathname: pathnamePart || "/",
    search: searchPart ? `?${searchPart}` : "",
    hash: hashPart ? `#${hashPart}` : ""
  };
}

function isExactNavigationTarget(currentLocation, to) {
  const target = normalizeNavigationTarget(to);
  if (!target) {
    return false;
  }

  const parsed = parseNavigationTarget(target);
  const currentPathname = String(currentLocation.pathname || "");
  const currentSearch = String(currentLocation.search || "");
  const currentHash = String(currentLocation.hash || "");

  return (
    parsed.pathname === currentPathname &&
    parsed.search === currentSearch &&
    parsed.hash === currentHash
  );
}

function handleSelfNavigationReload(event, currentLocation, to) {
  if (!event || event.defaultPrevented) {
    return;
  }

  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  if (!isExactNavigationTarget(currentLocation, to)) {
    return;
  }

  event.preventDefault();
  if (typeof window !== "undefined") {
    window.location.reload();
  }
}

function getNavItemId(item, fallbackIndex = "0") {
  return String(item?.key || `${item?.label || "nav"}-${item?.path || ""}-${fallbackIndex}`);
}

function dismissNavFocus() {
  if (typeof document === "undefined") {
    return;
  }

  const activeElement = document.activeElement;
  if (activeElement && typeof activeElement.blur === "function") {
    activeElement.blur();
  }
}

export default function StorefrontLayout({
  apiBaseUrl,
  language,
  currency,
  onChangeLanguage,
  onChangeCurrency,
  showPricing = true,
  disableSeoAutofill = false,
  children
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return Boolean(localStorage.getItem("ft_admin_token"));
  });
  const [navItems, setNavItems] = useState(() => getDefaultLayoutNavItems());
  const [configShowPrice, setConfigShowPrice] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [privacyMode, setPrivacyMode] = useState(() => readPrivacyMode());
  const [privacyPromptForcedOpen, setPrivacyPromptForcedOpen] = useState(false);

  useEffect(() => {
    function syncAuthState() {
      setIsAuthenticated(Boolean(localStorage.getItem("ft_admin_token")));
    }

    window.addEventListener(ADMIN_AUTH_UPDATED_EVENT, syncAuthState);
    window.addEventListener(USER_AUTH_UPDATED_EVENT, syncAuthState);
    window.addEventListener("storage", syncAuthState);

    return () => {
      window.removeEventListener(ADMIN_AUTH_UPDATED_EVENT, syncAuthState);
      window.removeEventListener(USER_AUTH_UPDATED_EVENT, syncAuthState);
      window.removeEventListener("storage", syncAuthState);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadStorefrontConfig() {
      if (!apiBaseUrl) {
        return;
      }

      try {
        const response = await fetch(`${apiBaseUrl}/storefront/config`);
        if (!response.ok) {
          throw new Error("Failed to load storefront config");
        }

        const data = await response.json();
        if (cancelled) {
          return;
        }

        if (Array.isArray(data.navMenu) && data.navMenu.length) {
          setNavItems(normalizeNavTree(data.navMenu));
        } else {
          setNavItems(getDefaultLayoutNavItems());
        }

        if (data.storeSettings && typeof data.storeSettings === "object") {
          const title = String(data.storeSettings.storefrontTitle || "").trim();

          if (disableSeoAutofill) {
            setConfigShowPrice(data.showPrice !== false);
            return;
          }

          const seoTitle = String(data.storeSettings.seo?.metaTitle || "").trim();
          const seoDescription = String(data.storeSettings.seo?.metaDescription || "").trim();
          const seoKeywords = String(data.storeSettings.seo?.keywords || "").trim();
          const seoCanonicalUrl = String(data.storeSettings.seo?.canonicalUrl || "").trim();
          const seoRobots = String(data.storeSettings.seo?.robots || "").trim();
          const ogTitle = String(data.storeSettings.seo?.ogTitle || "").trim();
          const ogDescription = String(data.storeSettings.seo?.ogDescription || "").trim();
          const ogImage = String(data.storeSettings.seo?.ogImage || "").trim();
          const twitterCard = String(data.storeSettings.seo?.twitterCard || "").trim();
          const twitterSite = String(data.storeSettings.seo?.twitterSite || "").trim();
          const resolvedTitle = seoTitle || title || "LOYA SPORT";
          const resolvedDescription = seoDescription || "";

          const ensureMetaTag = (selector, attr, content) => {
            if (!content) {
              return;
            }

            let element = document.querySelector(selector);
            if (!element) {
              element = document.createElement("meta");
              element.setAttribute(attr, selector.match(/\"(.+?)\"/)?.[1] || "");
              document.head.appendChild(element);
            }
            element.setAttribute("content", content);
          };

          const descriptionMeta = document.querySelector('meta[name="description"]');
          if (descriptionMeta && seoDescription) {
            descriptionMeta.setAttribute("content", seoDescription);
          }

          const keywordsMeta = document.querySelector('meta[name="keywords"]');
          if (keywordsMeta && seoKeywords) {
            keywordsMeta.setAttribute("content", seoKeywords);
          }

          if (seoCanonicalUrl) {
            let canonical = document.querySelector('link[rel="canonical"]');
            if (!canonical) {
              canonical = document.createElement("link");
              canonical.setAttribute("rel", "canonical");
              document.head.appendChild(canonical);
            }
            canonical.setAttribute("href", seoCanonicalUrl);
          }

          ensureMetaTag('meta[name="robots"]', "name", seoRobots);
          ensureMetaTag('meta[property="og:title"]', "property", ogTitle || resolvedTitle);
          ensureMetaTag('meta[property="og:description"]', "property", ogDescription || resolvedDescription);
          ensureMetaTag('meta[property="og:image"]', "property", ogImage);
          ensureMetaTag('meta[name="twitter:card"]', "name", twitterCard || "summary_large_image");
          ensureMetaTag('meta[name="twitter:title"]', "name", ogTitle || resolvedTitle);
          ensureMetaTag('meta[name="twitter:description"]', "name", ogDescription || resolvedDescription);
          ensureMetaTag('meta[name="twitter:image"]', "name", ogImage);
          ensureMetaTag('meta[name="twitter:site"]', "name", twitterSite);
        }

        setConfigShowPrice(data.showPrice !== false);
      } catch (_err) {
        if (!cancelled) {
          setNavItems(getDefaultLayoutNavItems());
          setConfigShowPrice(true);
        }
      }
    }

    loadStorefrontConfig();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, disableSeoAutofill]);

  const effectiveShowPricing = useMemo(
    () => showPricing && configShowPrice,
    [showPricing, configShowPrice]
  );

  const groupedNavItems = useMemo(() => buildGroupedNavItems(navItems), [navItems]);
  const activeNavId = useMemo(() => {
    const flatItems = collectNavNodes(groupedNavItems);
    const matchedItem = flatItems.find(({ item }) => matchesNavPath(location, item.path));
    return matchedItem ? getNavItemId(matchedItem.item, matchedItem.index) : "";
  }, [groupedNavItems, location]);

  function isNavItemActive(item, fallbackIndex = "0") {
    if (!item || !activeNavId) {
      return false;
    }

    return getNavItemId(item, fallbackIndex) === activeNavId;
  }

  function handleSearchProduct() {
    setSearchOpen(true);
  }

  function handleBookmarkSite() {
    if (typeof window === "undefined") {
      return;
    }

    const title = document.title || "LOYA SPORT";
    const url = window.location.origin || window.location.href;

    try {
      if (window.sidebar && typeof window.sidebar.addPanel === "function") {
        window.sidebar.addPanel(title, url, "");
        return;
      }

      if (window.external && typeof window.external.AddFavorite === "function") {
        window.external.AddFavorite(url, title);
        return;
      }
    } catch (_err) {
      // Ignore and fallback to manual instruction.
    }

    window.alert(t("bookmarkHint"));
  }

  // 导航栏界面和逻辑替换为 StorefrontPage.js 方案（不含logo区域）
  function renderNavItems(items, depth = 1, parentIndex = "") {
    if (!Array.isArray(items) || !items.length) {
      return null;
    }

    return items.filter((item) => item.visible !== false).map((item, index) => {
      const indexPath = parentIndex ? `${parentIndex}-${index}` : String(index);
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      const parentActive =
        isNavItemActive(item, indexPath) ||
        collectNavNodes(item.children, indexPath).some(({ item: child, index: childIndex }) => isNavItemActive(child, childIndex));
      const navKey = item.key || `${item.label}-${item.path}-${indexPath}`;

      if (!hasChildren) {
        return (
          <Link
            key={navKey}
            to={item.path}
            className={parentActive ? "active" : ""}
            onClick={(event) => {
              dismissNavFocus();
              // 保持页面锚点跳转逻辑
              if (item.path && item.path.startsWith("/#")) {
                event.preventDefault();
                const targetId = item.path.slice(2);
                const target = document.getElementById(targetId);
                if (target) {
                  target.scrollIntoView({ behavior: "smooth", block: "start" });
                  window.history.replaceState(null, "", item.path);
                }
              } else {
                handleSelfNavigationReload(event, location, item.path);
              }
            }}
          >
            {item.label}
          </Link>
        );
      }

      return (
        <div key={navKey} className={`commercehub-nav-item has-children nav-depth-${depth}${parentActive ? " active" : ""}`}>
          <Link
            to={item.path}
            className={parentActive ? "active" : ""}
            onClick={(event) => {
              dismissNavFocus();
              if (item.path && item.path.startsWith("/#")) {
                event.preventDefault();
                const targetId = item.path.slice(2);
                const target = document.getElementById(targetId);
                if (target) {
                  target.scrollIntoView({ behavior: "smooth", block: "start" });
                  window.history.replaceState(null, "", item.path);
                }
              } else {
                handleSelfNavigationReload(event, location, item.path);
              }
            }}
          >
            {item.label}
          </Link>
          <span className="commercehub-nav-caret" aria-hidden="true">▾</span>
          <div className="commercehub-submenu" role="menu" aria-label={`${item.label} submenu`}>
            {renderNavItems(item.children, depth + 1, indexPath)}
          </div>
        </div>
      );
    });
  }

  const breadcrumbItems = useMemo(
    () => getBreadcrumbItems(location.pathname, location.search, navItems, location.state),
    [location.pathname, location.search, location.state, navItems]
  );
  const shouldShowBreadcrumb =
    location.pathname !== "/" &&
    location.pathname !== "/login" &&
    !location.pathname.startsWith("/admin");
  const isStorefrontHome = location.pathname === "/";
  const useCoolContentBackground = ["/", "/products", "/about", "/contact"].includes(location.pathname);
  const privacyPromptOpen = privacyPromptForcedOpen;
  const privacyEnhanced = privacyMode === PRIVACY_MODE_ENHANCED;

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    function openPrivacySettings() {
      setPrivacyPromptForcedOpen(true);
    }

    window.addEventListener(PRIVACY_SETTINGS_OPEN_EVENT, openPrivacySettings);
    return () => {
      window.removeEventListener(PRIVACY_SETTINGS_OPEN_EVENT, openPrivacySettings);
    };
  }, []);

  function handlePrivacyModeSelection(nextMode) {
    const normalized = String(nextMode || "").trim().toLowerCase();
    if (normalized !== PRIVACY_MODE_ENHANCED && normalized !== PRIVACY_MODE_BASIC) {
      return;
    }

    setPrivacyMode(normalized);
    writePrivacyMode(normalized);
    setPrivacyPromptForcedOpen(false);

    if (normalized === PRIVACY_MODE_BASIC && typeof window !== "undefined") {
      window.localStorage.removeItem(SEARCH_HISTORY_KEY);
    }
  }

  function handleOpenPrivacySettings() {
    setPrivacyPromptForcedOpen(true);
  }

  return (
    <>
      {/* 保留面包屑、隐私弹窗、主内容、底部和搜索弹窗，不再渲染任何 header/nav/logo/主菜单相关内容 */}
      {shouldShowBreadcrumb ? (
        <div className="commercehub-breadcrumb-bar">
          <div className="container commercehub-breadcrumb-inner">
            <nav className="commercehub-breadcrumb-trail" aria-label={t("ariaBreadcrumbNavigation")}>
              {breadcrumbItems.map((item, index) => {
                const isLast = index === breadcrumbItems.length - 1;
                return (
                  <span
                    key={`${item.label}-${index}`}
                    className={`commercehub-crumb${isLast ? " is-current" : ""}`}
                  >
                    {item.path ? (
                      <Link
                        to={isLast ? { pathname: location.pathname, search: location.search, hash: location.hash } : item.path}
                        state={isLast ? location.state : undefined}
                        className="commercehub-crumb-text"
                        title={item.label}
                        onClick={(event) => {
                          const target = isLast
                            ? { pathname: location.pathname, search: location.search, hash: location.hash }
                            : item.path;
                          handleSelfNavigationReload(event, location, target);
                        }}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <span className="commercehub-crumb-text" title={item.label}>{item.label}</span>
                    )}
                    {!isLast ? <span className="commercehub-crumb-separator" aria-hidden="true">›</span> : null}
                  </span>
                );
              })}
            </nav>
          </div>
        </div>
      ) : null}

      {privacyPromptOpen ? (
        <div className="privacy-consent-backdrop" role="dialog" aria-modal="true" aria-label={t("ariaPrivacyPreference")}>
          <section className="privacy-consent-card">
            <h3>{t("privacyTitle")}</h3>
            <p>
              {t("privacyBody")}
            </p>
            <ul>
              <li>{t("privacyOptionEnhanced")}</li>
              <li>{t("privacyOptionBasic")}</li>
            </ul>
            <div className="privacy-consent-actions">
              <button type="button" className="button" onClick={() => handlePrivacyModeSelection(PRIVACY_MODE_ENHANCED)}>
                {t("privacyAccept")}
              </button>
              <button type="button" className="button ghost" onClick={() => handlePrivacyModeSelection(PRIVACY_MODE_BASIC)}>
                {t("privacyReject")}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      <main className={`container page${useCoolContentBackground ? " page-cool-content" : ""}${isStorefrontHome ? "" : " storefront-subpage-shell"}`}>{children}</main>

      <ProductSearchOverlay
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        apiBaseUrl={apiBaseUrl}
        privacyEnhanced={privacyEnhanced}
      />
    </>
  );
}
